import { PureComponent, ReactNode } from 'react';
import getCareCanvasTemplates from '../../../../../api/getCareCanvasTemplates';
import { imageToBase64, uploadImage } from './utils';
import { notification } from 'antd';
import { captureError, captureErrorString } from '../../../../../pages/captureError';
import { track } from '../../../../../tracking';
import { v4 } from 'uuid';
import uploadCareCanvasTemplate from '../../../../../api/uploadCareCanvasTemplate';
import { CareCanvasTemplate } from '../../../../../store/types/CareCanvas';
import { DEFAULT_IMAGE } from './PPCareCanvas';

type Props = {
    onCancel: () => void;
    uploadedImage?: string;
    finalImage?: string;
    markerAreaState: any;
    docid: string;
};

type State = {
    input?: string;
    selectedTemplateId: string;
    isSaved?: boolean;
    selectedTab: 'NEW' | 'UPDATE';
    templates: CareCanvasTemplate[];
    isSavingCanvas?: boolean;
};

class CreateTemplate extends PureComponent<Props, State> {
    DEFAULT_ID = '0';
    state: State = {
        selectedTemplateId: this.DEFAULT_ID,
        selectedTab: 'NEW',
        templates: [],
    };

    componentDidMount = async () => {
        const { docid } = this.props;
        try {
            const { templates } = await getCareCanvasTemplates(docid);
            this.setState({ templates: templates || [] });
        } catch (error) {
            notification.error({
                message: 'Failed to get canvas templates',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'getCareCanvasTemplates', what: (error as Error).message });
        }
    };

    selectUpdate = (): void => {
        this.setState({
            selectedTab: 'UPDATE',
        });
    };

    selectNew = (): void => {
        this.setState({
            selectedTab: 'NEW',
        });
    };

    onSave = (): void => {
        const { templates } = this.state;
        if (this.isSaveBtDisabled()) {
            return;
        }

        const { input, selectedTemplateId } = this.state;

        if (input) {
            this.onSaveTemplate(input);
            this.setState({
                isSaved: true,
            });
        } else if (selectedTemplateId) {
            const gT = templates.find((t) => t._id === selectedTemplateId);
            if (gT) {
                this.onSaveTemplate(gT.name, selectedTemplateId);
                this.setState({
                    isSaved: true,
                });
            }
        }
    };

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const input = e.target.value;
        this.setState({
            input: input,
            selectedTemplateId: this.DEFAULT_ID,
            isSaved: false,
        });
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            this.onSave();
        }
    };

    onTemplateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedTemplateId = e.target.value;
        this.setState({
            selectedTemplateId,
            input: undefined,
            isSaved: false,
        });
    };

    isSaveBtDisabled = (): boolean => {
        const { input, selectedTemplateId, selectedTab } = this.state;

        return selectedTab === 'NEW'
            ? !input || input.length === 0
            : selectedTemplateId === this.DEFAULT_ID;
    };

    onSaveTemplate = async (name: string, id?: string) => {
        const { uploadedImage, finalImage, markerAreaState, onCancel, docid } = this.props;
        if (!uploadedImage || !finalImage) {
            return;
        }
        const uuid = v4();
        this.setState({
            isSavingCanvas: true,
        });
        const uploadedImageDataUrl = uploadedImage.includes(DEFAULT_IMAGE)
            ? await imageToBase64(uploadedImage)
            : uploadedImage;
        const finalImageDataUrl = finalImage.includes(DEFAULT_IMAGE)
            ? await imageToBase64(finalImage)
            : finalImage;
        if (!uploadedImageDataUrl || !finalImageDataUrl) {
            notification.error({
                message: 'Failed to save template',
                duration: 5,
            });
            this.setState({
                isSavingCanvas: false,
            });
            return;
        }
        const uploadedImageUrl = await uploadImage(uploadedImageDataUrl, `base-image-${uuid}`);
        const finalImageUrl = await uploadImage(finalImageDataUrl, `final-image-${uuid}`);
        if (!uploadedImageUrl || !finalImageUrl) {
            notification.error({
                message: 'Failed to upload images',
                duration: 5,
            });
            this.setState({
                isSavingCanvas: false,
            });
            return;
        }
        const careCanvasTemplate: CareCanvasTemplate = {
            _id: id || uuid,
            docid: docid,
            name: name,
            uploaded_image: uploadedImageUrl,
            final_image: finalImageUrl,
            marker_area_state: markerAreaState,
            created_at: new Date().toISOString(),
            type: 'template',
        };
        try {
            const { success, error } = await uploadCareCanvasTemplate(careCanvasTemplate);
            if (success) {
                notification.success({
                    message: 'Canvas template saved successfully',
                    duration: 5,
                });
                setTimeout(() => {
                    onCancel();
                }, 500);
            } else {
                notification.error({
                    message: 'Failed to save canvas',
                    duration: 5,
                });
                captureErrorString(error || 'Failed to save canvas');
                track('ERROR', { on: 'uploadCareCanvas', what: error || 'Failed to save canvas' });
            }
        } catch (error) {
            notification.error({
                message: 'Failed to save canvas',
                duration: 5,
            });
            captureError(error as Error);
            track('ERROR', { on: 'uploadCareCanvasTemplate', what: (error as Error).message });
        } finally {
            this.setState({
                isSavingCanvas: false,
            });
        }
    };

    render(): ReactNode {
        const { onCancel } = this.props;
        const {
            input,
            selectedTemplateId,
            isSaved,
            selectedTab,
            templates,
            isSavingCanvas,
        } = this.state;

        const isDisabled = this.isSaveBtDisabled();

        return (
            <div className="rounded-8 py-16 global-template-popup popup flex flex-col border border-neutral-7 w-462">
                <div className="pl-16 pr-24 h-full w-full flex items-center justify-end ml-8">
                    <div className="flex items-center space-x-4 cursor-pointer" onClick={onCancel}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 320 512"
                            className="w-16 h-16 fill-neutral-3 hover:fill-neutral-1"
                        >
                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                        </svg>
                    </div>
                </div>
                <div className="flex flex-col  items-center px-16 w-full">
                    <div className="w-full border-b border-neutral-8 select-none">
                        <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8">
                            <div
                                className={
                                    selectedTab === 'NEW'
                                        ? 'border-b-2 border-actionPrimary  text-13 font-700'
                                        : 'border-b-2 border-transparent text-13 font-700'
                                }
                                onClick={this.selectNew}
                            >
                                <p
                                    className={
                                        selectedTab === 'NEW'
                                            ? 'pb-8 text-actionPrimary text-13 font-700'
                                            : 'pb-8 text-neutral-3 text-13 font-700'
                                    }
                                >
                                    New Template
                                </p>
                            </div>
                            <div
                                className={
                                    selectedTab === 'UPDATE'
                                        ? 'border-b-2 mx-16 border-actionPrimary  text-13 font-700'
                                        : 'border-b-2 mx-16 border-transparent text-13 font-700'
                                }
                                onClick={this.selectUpdate}
                            >
                                <p
                                    className={
                                        selectedTab === 'UPDATE'
                                            ? 'pb-8 text-actionPrimary text-13 font-700'
                                            : 'pb-8 text-neutral-3 text-13 font-700'
                                    }
                                >
                                    Update existing Template
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="flex w-full mt-16 mb-8">
                        <div className="pr-12 flex flex-col w-full justify-between items-center">
                            <div className="w-full ">
                                {selectedTab === 'NEW' && (
                                    <div className="pt-1 bg-white ether-spaced-input-component text-neutral-5 border border-neutral-6">
                                        <div className="flex items-center w-full">
                                            <div className="flex z-0 w-full">
                                                <input
                                                    placeholder="Template Name"
                                                    value={input}
                                                    autoFocus
                                                    onChange={this.onInputChange}
                                                    onKeyDown={this.onKeyDown}
                                                    className="text-14 font-400"
                                                ></input>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {selectedTab === 'UPDATE' && (
                                    <div className="px-16 space-x-12 py-7 mt-1 overflow-auto border border-neutral-5 rounded-4 bg-white">
                                        <select
                                            value={selectedTemplateId}
                                            onChange={this.onTemplateSelectChange}
                                            className="form-control w-full text-neutral-black font-600 focus:outline-none"
                                        >
                                            <option value={this.DEFAULT_ID}>-</option>
                                            {templates.map((t) => (
                                                <option key={t._id} value={t._id}>
                                                    {t.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={() => (!isSaved ? this.onSave() : undefined)}
                            className="primary-btn btn--small px-12 py-6"
                            disabled={isDisabled}
                        >
                            <p className="text-center w-full">
                                {selectedTab === 'NEW' ? 'Save' : 'Update'}
                            </p>

                            {isSavingCanvas ? (
                                <div className="flex items-center justify-center ml-8">
                                    <svg
                                        className="w-16 h-16 fill-white animate-spin"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                    </svg>
                                </div>
                            ) : isSaved ? (
                                <div className="flex items-center justify-center ml-8">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        className="fill-white"
                                    >
                                        <path d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"></path>
                                    </svg>
                                </div>
                            ) : null}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default CreateTemplate;
